import { isDefined, isNonEmpty, isNotDefined } from '@sgme/fp';
import { ProductNodeBuilderProps } from 'components/common-form/builder/form/node/ProductNodeBuilder.model';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { ListField } from 'components/shared/ListField/ListField';
import type { IntlKey } from 'locales';
import type { DealSplitData } from 'models/forms/DealSplitData';
import type { SingleCustodianDealSplitFieldData, SingleCustodianDealSplitFieldMetadata } from 'models/forms/fields';
import { ChangeEvent, FC, useCallback } from 'react';

export const SingleCustodianDealSplitField: FC<ProductNodeBuilderProps> = ({
  node: { id },
  className,
  workspaceType,
}) => {
  const [value, setValue] = useData<SingleCustodianDealSplitFieldData>(id);
  const custodianValue = isNonEmpty(value) ? value[0].Custodian : undefined;
  const metadata = useFieldMetadata<SingleCustodianDealSplitFieldMetadata>(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);

  const onCustodianSelected = useCallback(
    (event: ChangeEvent<HTMLSelectElement>) => {
      const custodian = event.target.value;
      if (metadata && isNonEmpty(custodian) && isDefined(metadata.notionalToSplit)) {
        const newValue: DealSplitData[] = [{ Custodian: custodian, NotionalAmountInGrid: metadata.notionalToSplit }];
        setValue(newValue);
      } else {
        setValue(undefined);
      }
    },
    [metadata, setValue],
  );

  if (isNotDefined(metadata)) {
    return <></>;
  }

  const tooltipKey = metadata.hasTooltip ? (`form.${metadata.translationKey}.tooltip` as IntlKey) : undefined;

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${metadata.translationKey}` as IntlKey}
      workspaceType={workspaceType}
      tooltipLocation={metadata.tooltipLocation}
      tooltipKey={tooltipKey}
      className={patchedFieldClassName}
    >
      <ListField
        id={id}
        items={metadata.availableCustodians ?? []}
        value={
          !metadata.availableCustodians?.find(custodians => custodians.value === custodianValue)
            ? undefined
            : custodianValue
        }
        onChange={onCustodianSelected}
        isReadOnly={metadata.isReadOnly}
        className={className}
        hasEmptyItem
      />
    </BlockOrientationFormGroup>
  );
};
